<template>
  <div>
    <ThemeDefaultMenuBreadcrumb v-if="breadcrumbItem" :items="breadcrumbItem" />
    <div class="tw-bg-primary-opacity-2 lg:tw-py-11 tw-py-4">
      <ThemeDefaultContainerPage
        v-if="promotion"
        class="promotion-detail-content"
      >
        <div
          class="tw-flex tw-flex-col md:tw-flex-row tw-bg-white tw-rounded-lg tw-p-4 tw-gap-4"
        >
          <div
            class="tw-w-full md:tw-w-5/12 tw-mb-0 tw-relative tw-overflow-hidden"
          >
            <BadgeRecommended v-if="promotion.recommended" />
            <div class="tw-aspect-square tw-relative">
              <MediaImageLazyLoad
                :src="promotion?.imageCover?.large || ''"
                :placeholder-src="promotion?.imageCover?.tiny || ''"
                :alt="promotion.title"
                custom-class="tw-rounded-lg"
                :ratio="1 / 1"
                seo-optimize
              />
            </div>
          </div>
          <div
            class="tw-w-full md:tw-w-7/12 tw-min-w-0 tw-flex tw-flex-col tw-items-start"
          >
            <h1
              class="tw-text-mb-title-1 lg:tw-text-dt-title-1 tw-text-black tw-break-words tw-w-full"
            >
              {{ promotion.title }}
            </h1>
            <DisplayHTMLContent
              class="tw-mt-2 tw-mb-3 tw-break-words tw-text-mb-body lg:tw-text-dt-body tw-text-dark-gray"
              :model-value="promotion.description || ''"
            />
            <div v-if="promotion.options" class="tw-w-full tw-flex tw-flex-col">
              <DisplayPrice
                :options="promotion.options || []"
                :show-options="promotion.showProductOptions"
                :min-price-option="promotion.minPriceOption"
                :max-price-option="promotion.maxPriceOption"
                :min-price-of-discounted-option="
                  promotion.minPriceOfDiscountedOption
                "
                :max-price-of-discounted-option="
                  promotion.maxPriceOfDiscountedOption
                "
                :price="promotion.price"
                :price-of-discounted="promotion.priceOfDiscounted"
                single-product-discount-container-class="tw-flex-row-reverse tw-gap-x-2 tw-items-center"
                discounted-price-range-container-class="tw-w-full tw-flex-row-reverse tw-flex-wrap tw-justify-end lg:tw-gap-3 tw-items-center"
                class="tw-mb-5"
                :show-border-option-name="false"
                :show-margin-between-options="false"
                options-container-class="tw-grid tw-grid-cols-2 tw-mb-3 last:tw-mb-5"
                discounted-class="tw-line-through tw-text-gray lg:tw-text-dt-body-small tw-text-mb-body-small tw-justify-end tw-items-end"
                option-price-class="tw-text-mb-title-5 lg:tw-text-dt-title-5 tw-text-black tw-justify-end tw-items-end"
                option-name-class="tw-text-mb-body-small lg:tw-text-dt-body"
                option-price-container-class="tw-text-right"
                option-discounted-container-class="tw-flex-row-reverse tw-items-center tw-gap-2"
                regular-option-price-class="tw-text-mb-title-5 lg:tw-text-dt-title-5"
                main-price-class="tw-text-mb-title-3 lg:tw-text-dt-title-3 tw-justify-start tw-relative tw--top-0.5"
                custom-horizontal-line="!tw-mb-5"
              />
              <div ref="mobileCtaRef" data-allow-mismatch>
                <ThemeDefaultButtonCallToAction
                  :enable-button="{
                    facebook: promotion.enableButton.facebook,
                    line: promotion.enableButton.line,
                    whatsapp: promotion.enableButton.whatsapp,
                    tel: promotion.enableButton.tel,
                    callToAction: promotion.enableButton.callToAction,
                    callToActionTitle: promotion.callToActionTitle,
                    callToActionIcon: promotion.callToActionIcon,
                    callToActionLink: promotion.callToActionLink,
                  }"
                  :event-name="eventButtonName"
                  button-class="!tw-h-12"
                  footer-class="tw-h-20 tw-px-4 tw-py-4 tw-items-center"
                  :hide-social-title="isMobile"
                  :bottom-sheet-card-style="{ borderRadius: '12px 12px 0 0' }"
                  :show-footer="!isCtaVisible"
                />
              </div>
              <ThemeDefaultButtonShare
                :category-event-name="categoryEventName"
                :title-name="promotion.title"
                :event-name="eventShareName"
                button-class="tw-mt-5"
              />
            </div>
          </div>
        </div>
      </ThemeDefaultContainerPage>
    </div>
    <ThemeDefaultContainerPage>
      <div
        v-if="promotion.content"
        class="lg:tw-mt-11 tw-mt-4 lg:tw-max-w-[900px] tw-mx-auto"
      >
        <DisplayHTMLContent
          class="tw-text-dark-gray"
          :model-value="promotion.content"
        />
      </div>
    </ThemeDefaultContainerPage>

    <ThemeDefaultContainerPage
      v-if="promotion && relatedPromotions && relatedPromotions.length > 0"
      class="tw-pt-5 lg:tw-pb-11 tw-pb-8 lg:tw-max-w-[900px] tw-mx-auto lg:!tw-px-0"
    >
      <HorizontalLine custom-class="!tw-my-0 !tw-mb-5" />
      <h2 class="tw-text-mb-title-2 lg:tw-text-dt-title-2 tw-mb-5">
        {{ $t("components.promotion.related") }}
      </h2>
      <div
        class="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-4"
      >
        <div v-for="item in relatedPromotions.slice(0, 4)" :key="item._id">
          <ThemeDefaultCardPromotion
            title-tag="h3"
            title-class="lg:tw-text-dt-title-5 tw-text-mb-title-4"
            single-price-class="tw-text-mb-title-5"
            :image="item.imageCover"
            :title="item.title"
            :description="item.description"
            :show-promotion-options="item.showPromotionOptions"
            :price="item.price"
            :price-of-discounted="item.priceOfDiscounted"
            :max-price-option="item.maxPriceOption"
            :min-price-option="item.minPriceOption"
            :min-price-of-discounted-option="item.minPriceOfDiscountedOption"
            :max-price-of-discounted-option="item.maxPriceOfDiscountedOption"
            :recommended="item.recommended"
            :path="getPath({ componentName: 'Promotion', slug: item.slug })"
            image-width="112"
            image-height="112"
            mobile-horizontal
            hide-desktop-price
            @click="eventPromotionRelated(item.title)"
          />
        </div>
      </div>
    </ThemeDefaultContainerPage>
    <div v-else-if="promotion" class="tw-h-11" />
  </div>
</template>

<script setup lang="ts">
const {
  eventButtonName,
  promotion,
  relatedPromotions,
  breadcrumbItem,
  getPath,
  eventPromotionRelated,
  eventShareName,
  categoryEventName,
} = await useDetailPromotionPage();
const { isMobile } = useDevice();

const mobileCtaRef = ref<HTMLElement | null>(null);
const isCtaVisible = ref(true);

onMounted(() => {
  if (!mobileCtaRef.value) {
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        isCtaVisible.value = entry.isIntersecting;
      });
    },
    {
      threshold: 0.1,
    },
  );

  observer.observe(mobileCtaRef.value as unknown as Element);

  onUnmounted(() => {
    observer.disconnect();
  });
});
</script>

<style>
@media (min-width: 1280px) {
  .promotion-detail-content {
    max-width: 996px !important;
    padding-left: 3rem !important;
    padding-right: 3rem !important;
    margin-left: auto;
    margin-right: auto;
  }
}
</style>

<style scoped>
@media (min-width: 1024px) {
  :deep(.tw-flex-wrap > .tw-line-through) {
    font-size: var(--theme-font-text-dt-body-size);
    line-height: var(--theme-font-text-dt-body-line-height);
    font-weight: var(--thmem-font-text-dt-body-weight);
  }
}
</style>
